import React, { useState, useReducer, useEffect, useRef } from 'react';
import { useHistory } from '../../../hooks/useRouterCompat';
import { t, Trans } from '@lingui/macro';
import { X, Trash2, Mail, Globe, Settings, Save, Camera, MinusCircle } from 'lucide-react';
import { Workspace } from '../../../models/Workspace';
import { Fire } from '../../../services/FireClient';
import { UtilApp } from '../../../services/Utils/UtilApp';
import { My } from '../../../services/My';
import { pojo } from '../../../services/Utils/Util';
import { ResizeImage } from '../../../services/Utils/ImageResizer';
import { UUID } from '../../../models/local/Uid';
import { useToast } from '../../../components/ui/use-toast';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../../components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "../../../components/ui/alert-dialog";
import WSOpenAIKey from './WSOpenAIKey';
import { IconPlus } from '../../components/FigmaIcons';

interface WSAdminSettingsProps {
    workspace: Workspace;
    onUpdate?: (silent?: boolean) => void;
}

const WSAdminSettings: React.FC<WSAdminSettingsProps> = ({ workspace: initialWorkspace, onUpdate }) => {
    const [workspace, setWorkspace] = useState<Workspace>(() => pojo(initialWorkspace) as Workspace);
    const [inProgress, setInProgress] = useState(false);
    const { toast } = useToast();
    const history = useHistory();
    const [_, forceUpdate] = useReducer((x) => x + 1, 0);

    // Domain Modal State
    const [isDomainModalOpen, setIsDomainModalOpen] = useState(false);
    const [domainName, setDomainName] = useState("");
    const [domainTarget, setDomainTarget] = useState<'member' | 'guest' | 'autojoin'>('member');

    // Icon state
    const [iconFile, setIconFile] = useState<File | null>(null);
    const [iconPreview, setIconPreview] = useState<string>(initialWorkspace.iconUrl || "");
    const iconInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        // Sync with prop if it changes significantly, but usually we want to edit local copy
        if (initialWorkspace.id !== workspace.id) {
            setWorkspace(pojo(initialWorkspace) as Workspace);
        }
    }, [initialWorkspace.id]);

    const handleSave = async () => {
        if (!workspace) return;
        console.log('[WSAdminSettings] handleSave started', { id: workspace.id, name: workspace.name });
        setInProgress(true);
        try {
            // 10초 타임아웃 설정
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Save timeout after 10 seconds')), 10000)
            );

            console.log('[WSAdminSettings] Calling updateWorkspace...');

            // 아이콘 이미지가 변경된 경우 먼저 업로드
            if (iconFile) {
                const iconUrl = await new Promise<string | null>((resolve) => {
                    Fire.uploadFile(iconFile, `/workspaces/${workspace.id}/icon/${UUID.get()}.jpg`, (url) => {
                        resolve(url);
                    }, null);
                });
                workspace.iconUrl = iconUrl;
                setIconFile(null);
                if (iconUrl) setIconPreview(iconUrl);
            }

            await Promise.race([
                Fire.ws.updateWorkspace(workspace),
                timeoutPromise
            ]);
            console.log('[WSAdminSettings] updateWorkspace completed successfully');

            toast({ title: t`Workspace updated`, duration: 2000 });
            onUpdate?.(true);
        } catch (e) {
            console.error('[WSAdminSettings] Failed to update workspace:', e);
            toast({ title: t`Failed to update workspace` });
        } finally {
            console.log('[WSAdminSettings] handleSave finished, resetting inProgress');
            setInProgress(false);
        }
    };



    const deleteWorkspace = async () => {
        if (!workspace?.id) return;

        if (workspace.tier !== 'FREE' && workspace.tier !== 'PREMIUM_TRIAL' && workspace.tier) {
            toast({
                title: t`Cannot delete workspace. You must cancel your subscription first.`
            });
            // Optionally redirect to subscription page
            // history.push(`/ws-admin/subscription/${workspace.id}`);
            return;
        }

        setInProgress(true);
        try {
            await Fire.ws.deleteWorkspace(workspace.id);
            history.replace('/');
        } catch (e) {
            toast({ title: t`Failed to delete workspace` });
            setInProgress(false);
        }
    };

    // Domain Logic
    const deleteDomain = (domain: string, target: 'member' | 'guest' | 'autojoin') => {
        if (target === 'member') {
            const idx = workspace.emailDomains?.indexOf(domain) ?? -1;
            if (idx > -1) workspace.emailDomains.splice(idx, 1);
        } else if (target === 'guest') {
            const idx = workspace.guestDomains?.indexOf(domain) ?? -1;
            if (idx > -1) workspace.guestDomains.splice(idx, 1);
        } else if (target === 'autojoin') {
            const idx = workspace.autoJoinEmailDomains?.indexOf(domain) ?? -1;
            if (idx > -1) workspace.autoJoinEmailDomains.splice(idx, 1);
        }
        forceUpdate();
    };

    const isValidEmailDomain = (domain: string) => {
        const re = new RegExp('^[a-z0-9]+([\\-\\.]{1}[a-z0-9]+)*\\.[a-z]{2,5}$');
        return re.test(domain);
    };

    const addEmailDomain = () => {
        if (domainName.trim() === '') {
            toast({ title: t`Please enter a valid domain` });
            return;
        }
        const domain = domainName.trim().toLowerCase().replace('@', '');

        if (!workspace.emailDomains) workspace.emailDomains = [];
        if (!workspace.guestDomains) workspace.guestDomains = [];
        if (!workspace.autoJoinEmailDomains) workspace.autoJoinEmailDomains = [];

        if (domainTarget === 'member' && workspace.emailDomains.includes(domain)) {
            toast({ title: t`Domain is already added` }); return;
        }
        if (domainTarget === 'guest' && workspace.guestDomains.includes(domain)) {
            toast({ title: t`Domain is already added` }); return;
        }
        if (domainTarget === 'autojoin' && workspace.autoJoinEmailDomains.includes(domain)) {
            toast({ title: t`Domain is already added` }); return;
        }

        if (!isValidEmailDomain(domain)) {
            toast({ title: t`Invalid domain format` });
            return;
        }

        if (domainTarget === 'member') workspace.emailDomains.push(domain);
        else if (domainTarget === 'guest') workspace.guestDomains.push(domain);
        else if (domainTarget === 'autojoin') workspace.autoJoinEmailDomains.push(domain);

        setIsDomainModalOpen(false);
        setDomainName("");
        forceUpdate();
    };

    return (
        <div className="w-full p-4 md:p-6 lg:p-8 space-y-8">
            <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-muted border border-border">
                    <Settings className="h-6 w-6 text-card-foreground" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold tracking-tight"><Trans>Workspace Settings</Trans></h2>
                    <p className="text-sm text-muted-foreground"><Trans>Manage your workspace profile and preferences.</Trans></p>
                </div>
            </div>

            {/* General Profile */}
            <Card>
                <CardHeader>
                    <CardTitle><Trans>General Information</Trans></CardTitle>
                    <CardDescription><Trans>Visible to all members of the workspace.</Trans></CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Workspace Icon */}
                    <div className="space-y-2">
                        <Label><Trans>Workspace Icon</Trans></Label>
                        <div className="flex items-center gap-4">
                            <div
                                className="relative w-16 h-16 rounded-xl border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors group overflow-hidden shrink-0"
                                onClick={() => iconInputRef.current?.click()}
                            >
                                {iconPreview ? (
                                    <img src={iconPreview} alt="icon" className="w-full h-full object-cover rounded-xl" />
                                ) : (
                                    <div className="flex flex-col items-center gap-0.5 text-muted-foreground group-hover:text-primary transition-colors">
                                        <Camera className="w-5 h-5" />
                                        <span className="text-meta-sm font-medium"><Trans>Upload</Trans></span>
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Button variant="outline" size="sm" onClick={() => iconInputRef.current?.click()}>
                                    <Camera className="w-3.5 h-3.5 mr-1.5" />
                                    <Trans>Change Icon</Trans>
                                </Button>
                                {iconPreview && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7 text-xs justify-start"
                                        onClick={() => {
                                            setIconFile(null);
                                            setIconPreview("");
                                            setWorkspace({ ...workspace, iconUrl: null });
                                        }}
                                    >
                                        <MinusCircle className="w-3 h-3 mr-1" />
                                        <Trans>Remove</Trans>
                                    </Button>
                                )}
                            </div>
                            <input
                                ref={iconInputRef}
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                        const resized = await ResizeImage(file, 256);
                                        setIconFile(resized);
                                        setIconPreview(URL.createObjectURL(resized));
                                    }
                                    e.target.value = '';
                                }}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="ws-title"><Trans>Workspace Name</Trans></Label>
                        <Input
                            id="ws-title"
                            autoComplete="new-password"
                            data-lpignore="true"
                            value={workspace.name}
                            onChange={e => setWorkspace({ ...workspace, name: e.target.value })}
                            className="bg-muted border-border"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="ws-desc"><Trans>Description</Trans></Label>
                        <Textarea
                            id="ws-desc"
                            value={workspace.description}
                            onChange={e => setWorkspace({ ...workspace, description: e.target.value })}
                            placeholder={t`No description`}
                            className="resize-none min-h-[100px]"
                            maxLength={400}
                        />
                    </div>
                </CardContent>
                <CardFooter className="border-t px-6 py-4 bg-muted/50 flex justify-end">
                    <Button onClick={handleSave} disabled={inProgress}>
                        {inProgress ? <Trans>Saving...</Trans> : <><Save className="w-4 h-4 mr-2" /><Trans>Save Changes</Trans></>}
                    </Button>
                </CardFooter>
            </Card>

            {/* Access Control */}
            <Card>
                <CardHeader>
                    <CardTitle><Trans>Access & Permissions</Trans></CardTitle>
                    <CardDescription><Trans>Control how users can join your workspace.</Trans></CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">

                    {/* Shared Domain Modal */}
                    <Dialog open={isDomainModalOpen} onOpenChange={setIsDomainModalOpen}>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle><Trans>Add Email Domain</Trans></DialogTitle>
                                <DialogDescription>
                                    {domainTarget === 'autojoin' && <Trans>Users with this domain will automatically join as members.</Trans>}
                                    {domainTarget === 'member' && <Trans>Allow users with this domain to join as members.</Trans>}
                                    {domainTarget === 'guest' && <Trans>Allow users with this domain to join as guests.</Trans>}
                                </DialogDescription>
                            </DialogHeader>
                            <div className="grid gap-4 py-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="domain"><Trans>Domain</Trans></Label>
                                    <Input
                                        id="domain"
                                        placeholder="example.com"
                                        autoComplete="new-password"
                                        data-lpignore="true"
                                        value={domainName}
                                        onChange={e => setDomainName(e.target.value)}
                                        className="bg-muted border-border"
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addEmailDomain();
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setIsDomainModalOpen(false)}><Trans>Cancel</Trans></Button>
                                <Button onClick={addEmailDomain}><Trans>Add</Trans></Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    {/* 1. Auto-Join Domains */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <Label className="text-base font-medium"><Trans>Auto-Join Domains</Trans></Label>
                                <p className="text-xs text-muted-foreground mt-1"><Trans>Users signing up with these domains will automatically join the workspace as Members.</Trans></p>
                            </div>
                            <Button variant="outline" size="sm" onClick={() => { setDomainTarget('autojoin'); setIsDomainModalOpen(true); }}>
                                <IconPlus className="w-4 h-4 mr-2" /> <Trans>Add Domain</Trans>
                            </Button>
                        </div>

                        {(workspace.autoJoinEmailDomains?.length ?? 0) === 0 ? (
                            <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-xl border border-dashed text-center">
                                <Trans>No auto-join domains configured.</Trans>
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {(workspace.autoJoinEmailDomains || []).map(domain => (
                                    <Badge key={domain} variant="secondary" className="pl-2 pr-1 py-1 flex items-center gap-1 rounded-md">
                                        <Mail className="w-3 h-3 text-primary mr-1" />
                                        {domain}
                                        <Button variant="ghost" size="icon" className="h-4 w-4 ml-1 hover:bg-destructive/15 hover:text-destructive rounded-full" onClick={() => deleteDomain(domain, 'autojoin')}>
                                            <X className="w-3 h-3" />
                                        </Button>
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* 2. Workspace Join Policy */}
                    <div className="space-y-4 pt-6 border-t border-border">
                        <div>
                            <Label className="text-base font-medium"><Trans>Workspace Join Policy</Trans></Label>
                            <p className="text-xs text-muted-foreground mt-1"><Trans>Control who can join this workspace as a member.</Trans></p>
                        </div>
                        <Select
                            value={workspace.invitationPolicy || 'INVITE_ONLY'}
                            onValueChange={(val: any) => {
                                const update: any = { ...workspace, invitationPolicy: val };
                                // 허용 도메인 목록은 도메인 제한 모드에서만 의미 있음 (잔존 시 검색 노출 혼선 방지)
                                if (val !== 'ALLOW_LISTED_EMAIL_DOMAIN') {
                                    update.emailDomains = [];
                                }
                                setWorkspace(update);
                            }}
                        >
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="INVITE_ONLY"><Trans>Private — join by invitation link only (Default)</Trans></SelectItem>
                                <SelectItem value="ALLOW_LISTED_EMAIL_DOMAIN"><Trans>Restrict to specific domains</Trans></SelectItem>
                                <SelectItem value="ALLOW_ANY_EMAIL_DOMAIN"><Trans>Public — anyone can join freely</Trans></SelectItem>
                            </SelectContent>
                        </Select>

                        {(workspace.invitationPolicy || 'INVITE_ONLY') === 'INVITE_ONLY' && (
                            <p className="text-xs text-muted-foreground">
                                <Trans>Only people who open a valid invitation link can join as members. Joining by workspace ID search is not possible.</Trans>
                            </p>
                        )}
                        {workspace.invitationPolicy === 'ALLOW_ANY_EMAIL_DOMAIN' && (
                            <p className="text-xs text-amber-600">
                                <Trans>Anyone who knows the workspace ID can join as a member. Use this only for open workspaces.</Trans>
                            </p>
                        )}
                        {workspace.invitationPolicy === 'ALLOW_LISTED_EMAIL_DOMAIN' && (
                            <p className="text-xs text-muted-foreground">
                                <Trans>Users from the allowed domains also need an invitation link to join. Joining by workspace ID search is not possible.</Trans>
                            </p>
                        )}

                        {workspace.invitationPolicy === 'ALLOW_LISTED_EMAIL_DOMAIN' && (
                            <div className="pt-2">
                                <div className="flex items-center justify-between mb-3">
                                    <Label className="text-sm font-medium text-card-foreground bg-muted px-2 py-1 rounded-md"><Trans>Allowed Member Domains</Trans></Label>
                                    <Button variant="outline" size="sm" onClick={() => { setDomainTarget('member'); setIsDomainModalOpen(true); }} className="h-7 text-xs">
                                        <IconPlus className="w-3 h-3 mr-1" /> <Trans>Add</Trans>
                                    </Button>
                                </div>
                                {(workspace.emailDomains?.length ?? 0) === 0 ? (
                                    <div className="text-sm text-destructive bg-destructive/5 p-3 rounded-xl border border-dashed border-destructive/40 text-center">
                                        <Trans>No domains added. No one can join or be invited as a member.</Trans>
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap gap-2">
                                        {(workspace.emailDomains || []).map(domain => (
                                            <Badge key={domain} variant="outline" className="pl-2 pr-1 py-1 flex items-center gap-1 bg-background">
                                                <Globe className="w-3 h-3 text-muted-foreground mr-1" />
                                                {domain}
                                                <Button variant="ghost" size="icon" className="h-4 w-4 ml-1 hover:bg-destructive/15 hover:text-destructive rounded-full" onClick={() => deleteDomain(domain, 'member')}>
                                                    <X className="w-3 h-3" />
                                                </Button>
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* TODO: Guest Invitation Policy
                     * - 초대 링크를 받은 사람에게도 제한 정책을 표시해야 함
                     *   (getRoomInviteInfo API 응답에 guestInvitationPolicy를 포함하여 RoomInvitationPage에서 표시)
                     * - InviteLinkModal에는 이미 정책 안내가 구현됨
                     * - 정책 enforcement 로직 (Functions에서 실제 가입 차단) 구현 필요
                     */}
                    {/* <div className="space-y-4 pt-6 border-t border-border">
                        <div>
                            <Label className="text-base font-medium"><Trans>Guest Invitation Policy</Trans></Label>
                            <p className="text-xs text-muted-foreground mt-1"><Trans>Control which email domains can be invited as Guests, or disable guest invitations entirely.</Trans></p>
                        </div>
                        <Select
                            value={workspace.guestInvitationPolicy || 'ALLOW_ALL'}
                            onValueChange={(val: any) => setWorkspace({ ...workspace, guestInvitationPolicy: val })}
                        >
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="ALLOW_ALL"><Trans>Allow any domain (Default)</Trans></SelectItem>
                                <SelectItem value="RESTRICT_TO_DOMAINS"><Trans>Restrict to specific domains</Trans></SelectItem>
                                <SelectItem value="DISALLOW_ALL"><Trans>Disallow all guests</Trans></SelectItem>
                            </SelectContent>
                        </Select>

                        {workspace.guestInvitationPolicy === 'RESTRICT_TO_DOMAINS' && (
                            <div className="pt-2">
                                <div className="flex items-center justify-between mb-3">
                                    <Label className="text-sm font-medium text-card-foreground bg-muted px-2 py-1 rounded-md"><Trans>Allowed Guest Domains</Trans></Label>
                                    <Button variant="outline" size="sm" onClick={() => { setDomainTarget('guest'); setIsDomainModalOpen(true); }} className="h-7 text-xs">
                                        <IconPlus className="w-3 h-3 mr-1" /> <Trans>Add</Trans>
                                    </Button>
                                </div>
                                {(workspace.guestDomains?.length ?? 0) === 0 ? (
                                    <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-xl border border-dashed text-center">
                                        <Trans>No domains added. No one can be invited as a guest.</Trans>
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap gap-2">
                                        {(workspace.guestDomains || []).map(domain => (
                                            <Badge key={domain} variant="outline" className="pl-2 pr-1 py-1 flex items-center gap-1 bg-background">
                                                <Globe className="w-3 h-3 text-muted-foreground mr-1" />
                                                {domain}
                                                <Button variant="ghost" size="icon" className="h-4 w-4 ml-1 hover:bg-destructive/15 hover:text-destructive rounded-full" onClick={() => deleteDomain(domain, 'guest')}>
                                                    <X className="w-3 h-3" />
                                                </Button>
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div> */}

                </CardContent>
                <CardFooter className="border-t px-6 py-4 bg-muted/50 flex justify-end">
                    <Button onClick={handleSave} disabled={inProgress}>
                        {inProgress ? <Trans>Saving...</Trans> : <><Save className="w-4 h-4 mr-2" /><Trans>Save Changes</Trans></>}
                    </Button>
                </CardFooter>
            </Card>

            {/* OpenAI Key (Conditional) */}
            {(workspace.id === "odo_bang" || workspace.id === "jiranfamaily" || workspace.id === "test") && (
                <Card>
                    <CardHeader>
                        <CardTitle><Trans>Integrations</Trans></CardTitle>
                    </CardHeader>
                    <CardContent>
                        <WSOpenAIKey workspaceId={workspace.id} />
                    </CardContent>
                </Card>
            )}

            {/* Danger Zone */}
            <Card className="border-destructive/20">
                <CardHeader>
                    <CardTitle className="text-destructive"><Trans>Danger Zone</Trans></CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center justify-between">
                        <div className="space-y-1">
                            <h4 className="font-medium"><Trans>Delete this workspace</Trans></h4>
                            <p className="text-sm text-muted-foreground"><Trans>Once you delete a workspace, there is no going back. Please be certain.</Trans></p>
                        </div>

                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <Button variant="destructive"><Trash2 className="w-4 h-4 mr-2" /><Trans>Delete Workspace</Trans></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle><Trans>Are you absolutely sure?</Trans></AlertDialogTitle>
                                    <AlertDialogDescription>
                                        <Trans>This action cannot be undone. This will permanently delete the workspace <strong>{workspace.name}</strong> and remove all associated data.</Trans>
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
                                    <AlertDialogAction onClick={deleteWorkspace} className="bg-destructive hover:bg-destructive"><Trans>Delete</Trans></AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </div>
                </CardContent>
                <CardFooter className="border-t bg-destructive/15 px-6 py-4 flex justify-between items-center text-xs text-muted-foreground">
                    <span>
                        <Trans>{workspace.name} was created {UtilApp.dateStrToDay(workspace.created, My.user.lang)} and is managed by {workspace.owner.name}</Trans>
                    </span>
                </CardFooter>
            </Card>

        </div>
    );
};

export default WSAdminSettings;
